import Dexie, { type Table } from "dexie";

/**
 * The local store.
 *
 * Two jobs, and they matter more than they look:
 *
 * 1. Read-through cache. The app fetched /clubs/:id six times in one session
 *    because every screen asked independently. Now one asks, the rest read what
 *    is already there.
 *
 * 2. Offline. Whatever was last seen is on disk, so the app opens and shows a
 *    real screen with no network at all — rather than a spinner that never
 *    resolves at a ground with one bar.
 */

interface Cached { key: string; value: unknown; at: number }

export interface Pending {
  id: string;
  matchId: string;
  command: unknown;
  createdAt: number;
  tries: number;
  lastError?: string;
}

class Store extends Dexie {
  cache!: Table<Cached, string>;
  pending!: Table<Pending, string>;
  newMatches!: Table<PendingMatch, string>;
  constructor() {
    super("orbit");
    this.version(3).stores({
      pending: "id, matchId, createdAt",
      // Keyed on matchId: a match is created once, and a duplicate row would
      // mean two fixtures for one game.
      newMatches: "matchId, createdAt",
      cache: "key, at",
    });
  }
}

export const db = new Store();

/* ── cache ──────────────────────────────────────────────────────────────── */

const memory = new Map<string, { value: unknown; at: number }>();

export async function cacheGet<T>(key: string): Promise<{ value: T; at: number } | null> {
  const hot = memory.get(key);
  if (hot) return hot as { value: T; at: number };
  try {
    const row = await db.cache.get(key);
    if (!row) return null;
    memory.set(key, { value: row.value, at: row.at });
    return { value: row.value as T, at: row.at };
  } catch {
    return null;
  }
}

/** Force the next read of these keys to hit the network. */
export function invalidate(...keys: string[]): void {
  for (const k of keys) { revalidated.delete(k); memory.delete(k); }
}

export async function cacheSet(key: string, value: unknown): Promise<void> {
  const at = Date.now();
  memory.set(key, { value, at });
  try { await db.cache.put({ key, value, at }); } catch { /* quota, or private mode */ }
}

/**
 * Read from cache immediately, refresh in the background.
 *
 * `onFresh` fires only when the server answers with something different, so a
 * screen paints from disk in a millisecond and quietly corrects itself.
 */
/** When each key was last revalidated, so a tab switch does not refetch. */
const revalidated = new Map<string, number>();
const REVALIDATE_AFTER_MS = 15_000;

export async function swr<T>(
  key: string,
  fetcher: () => Promise<{ ok: boolean; data: T }>,
  onFresh: (value: T) => void,
): Promise<T | null> {
  const hit = await cacheGet<T>(key);

  // Squads and fixtures do not change between one tab tap and the next.
  // Without this the app refetched the club on every switch — seven times in
  // a single session, each one a round trip a scorer was waiting on.
  const last = revalidated.get(key) ?? 0;
  if (hit && Date.now() - last < REVALIDATE_AFTER_MS) return hit.value;

  void (async () => {
    try {
      revalidated.set(key, Date.now());
      const r = await fetcher();
      if (!r.ok) return;
      if (!hit || JSON.stringify(hit.value) !== JSON.stringify(r.data)) {
        await cacheSet(key, r.data);
        onFresh(r.data);
      }
    } catch {
      revalidated.delete(key);   // let the next attempt try again straight away
    }
  })();
  return hit ? hit.value : null;
}

/* ── outbox ─────────────────────────────────────────────────────────────── */

/**
 * A match created with no signal.
 *
 * Stored separately from deliveries because it is not a delivery: it has no
 * lease, it must be sent before anything queued against that match, and it can
 * only be sent once. The drain does these first, in order, for exactly that
 * reason.
 */
export interface PendingMatch {
  matchId: string;
  clubId: string;
  body: Record<string, unknown>;
  createdAt: number;
  tries: number;
}

export async function queueMatch(clubId: string, matchId: string,
                                 body: Record<string, unknown>): Promise<void> {
  await db.newMatches.put({ matchId, clubId, body, createdAt: Date.now(), tries: 0 });
}

export async function pendingMatches(): Promise<PendingMatch[]> {
  return db.newMatches.orderBy("createdAt").toArray();
}

export async function settleMatch(matchId: string): Promise<void> {
  await db.newMatches.delete(matchId);
}

export async function queue(matchId: string, command: unknown): Promise<string> {
  const id = crypto.randomUUID();
  await db.pending.put({ id, matchId, command, createdAt: Date.now(), tries: 0 });
  return id;
}

export async function pendingFor(matchId: string): Promise<Pending[]> {
  return db.pending.where("matchId").equals(matchId).sortBy("createdAt");
}

export async function settle(id: string): Promise<void> {
  await db.pending.delete(id);
}

export async function failed(id: string, error: string): Promise<void> {
  const row = await db.pending.get(id);
  if (row) await db.pending.put({ ...row, tries: row.tries + 1, lastError: error });
}

/**
 * iOS evicts IndexedDB under storage pressure, which for a scorer holding
 * unsent deliveries is the risk that matters. Ask, and say so if refused.
 */
export async function requestPersistence(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  if (await navigator.storage.persisted?.()) return true;
  return navigator.storage.persist();
}

/**
 * Everything still waiting to send, across every match.
 *
 * There was a per-match export and nothing that called it, so when a match
 * looked short of balls the only way to find out whether they were still on
 * the phone was to read the code. A rescue tool that cannot be reached is not
 * a rescue tool.
 */
export async function exportAllPending(): Promise<string> {
  const rows = await db.pending.toArray();
  return JSON.stringify({
    exportedAt: new Date().toISOString(),
    count: rows.length,
    // Grouped by match, because that is the question being asked: which game
    // is missing deliveries, and which ones.
    matches: [...new Set(rows.map((r) => r.matchId))].map((matchId) => ({
      matchId,
      pending: rows.filter((r) => r.matchId === matchId),
    })),
  }, null, 2);
}

